import { useState, useMemo, useCallback } from "react";
import { Helmet } from "react-helmet";
import { integrationService } from "/app/src/services";
import { Row, Col } from "antd";
import { useNavigate, useSearchParams } from "react-router-dom";
import Theme from "/app/src/components/generic/components/theme/Theme";
import Filters from "./filters";
import { useTranslation } from "react-i18next";
import { Integration, App } from "/app/src/models";
import { createColumnHelper } from "@tanstack/react-table";
import Table from "/app/src/components/generic/tables/table";
import { useSortUpgrade } from "/app/src/hooks/useSortUpgrade";
import { Theme as ThemeType } from "/app/src/types";
import DeleteButtonCell from "/app/src/components/generic/tables/deleteButtonCell";
import StatusIcon from "/app/src/components/generic/components/statusIcon";
import usePaginatedData from "/app/src/hooks/usePaginatedData";
import useQueryParams from "/app/src/hooks/useQueryParams";

// Uses shared Theme component (see /components/generic/components/theme/Theme.tsx)
interface ColumnAccessor {
  position: number;
  key: string;
  cell: (info) => React.ReactNode;
  header: () => React.ReactNode;
  id: string;
}
/**
 * Renders a list of integrations with associated filters, pagination, and sorting.
 *
 * @param {Object} props - The component props.
 * @param {App} props.app - The application object.
 * @param {ThemeType[]} [props.themes] - Optional array of theme objects.
 * @param {ThemeType[]} [props.types] - Optional array of types.
 * @param {boolean} [props.includeStatus=false] - Flag to include status column.
 * @param {boolean} [props.includeType=true] - Flag to include type column.
 * @param {ColumnAccessor[]} [props.customAccessors] - Array of custom column accessor definitions.
 * @returns {JSX.Element} The rendered integrations list component.
 */
export default function IntegrationsList({
  app,
  themes,
  types = [],
  includeStatus = false,
  includeType = true,
  customAccessors = [],
}: {
  app: App;
  themes?: ThemeType[];
  types?: ThemeType[];
  includeStatus?: boolean;
  includeType?: boolean;
  customAccessors?: ColumnAccessor[];
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [sort, setSort] = useSortUpgrade([]);
  const [searchString, setSearchString] = useState(
    searchParams.get("search") || "",
  );
  const [selectedTheme, setSelectedTheme] = useState("all");
  const [selectedType, setSelectedType] = useState("all");
  const columnHelper = createColumnHelper<Integration>();
  const [page, setPage] = useState(
    parseInt(searchParams.get("page") || "1") - 1,
  );
  const [pageSize, setPageSize] = useState(
    parseInt(searchParams.get("pageSize") || "25"),
  );
  const queryOptions = useMemo(
    () => ({ appId: app.id, theme: selectedTheme, number: selectedType }),
    [app.id, selectedTheme, selectedType],
  );

  const {
    data: integrations,
    count: integrationsCount,
    isFetching,
    refetch,
  } = usePaginatedData<Integration>({
    queryKey: ["integrations"],
    searchString,
    sort,
    page,
    pageSize,
    service: integrationService,
    options: queryOptions,
  });

  const { updatePage, updatePageSize } = useQueryParams({
    count: integrationsCount,
    isFetching,
    page,
    setPage,
    pageSize,
    setPageSize,
  });

  const refreshTable = useCallback(() => {
    refetch();
  }, [refetch]);

  const deleteIntegration = useCallback((id: number) => {
    return integrationService.deleteSingle(id);
  }, []);

  const columns = useMemo(() => {
    const cols = [
      columnHelper.accessor("name", {
        id: "name",
        cell: (info) => info.getValue(),
        header: () => t("translation:name"),
      }),
      columnHelper.accessor("id", {
        id: "delete",
        cell: (info) => (
          <DeleteButtonCell
            id={info.getValue()}
            refresh={refreshTable}
            deleteFn={deleteIntegration}
            headerText={t("translation:confirm_delete_integration")}
          />
        ),
        header: () => "",
        meta: {
          clickable: false,
          sortable: false,
        },
      }),
    ];
    if (includeType) {
      cols.splice(
        cols.length - 1,
        0,
        columnHelper.accessor("number", {
          id: "number",
          cell: (info) => info.getValue(),
          header: () => t("translation:type"),
        }),
      );
    }
    if (themes) {
      cols.splice(
        cols.length - 1,
        0,
        columnHelper.accessor("baseTable", {
          id: "baseTable",
          cell: (info) => <Theme value={info.getValue()} />,
          header: () => t("translation:theme"),
        }),
      );
    }
    if (includeStatus) {
      cols.splice(
        cols.length - 1,
        0,
        columnHelper.accessor("status".toString(), {
          id: "status",
          cell: (info) => {
            const value = Number(info.getValue());
            return <StatusIcon status={value} />;
          },
          header: () => t("translation:status"),
        }),
      );
    }
    customAccessors.forEach((accessor) => {
      cols.splice(
        accessor.position,
        0,
        columnHelper.accessor(accessor.key, {
          id: accessor.id,
          cell: accessor.cell,
          header: accessor.header,
        }),
      );
    });
    return cols;
  }, [
    columnHelper,
    customAccessors,
    deleteIntegration,
    includeStatus,
    includeType,
    refreshTable,
    t,
    themes,
  ]);

  /**
   *
   * @param integrationId number | string
   * @returns void
   * Navigates to the integration page
   */
  const goToIntegration = useCallback(
    (integrationId: number | string) => {
      navigate(`/apps/${app.id}/integrations/${integrationId}`);
    },
    [app.id, navigate],
  );

  return (
    <div>
      <Helmet>
        <title>{app.name} - ItemPath</title>
      </Helmet>
      <Row gutter={20}>
        <Col
          xs={{ span: 24, order: 2 }}
          sm={{ span: 24, order: 2 }}
          lg={{ span: 6, order: 1 }}
        >
          <Filters
            themes={themes}
            setSearchString={setSearchString}
            setSelectedTheme={setSelectedTheme}
            setSelectedType={setSelectedType}
            types={types}
          />
        </Col>
        <Col
          xs={{ span: 24, order: 1 }}
          sm={{ span: 24, order: 1 }}
          lg={{ span: 18, order: 2 }}
        >
          <Table
            loading={isFetching}
            rows={integrations}
            tableColumns={columns}
            length={integrationsCount}
            sort={sort}
            setSort={setSort}
            paginationEnabled={{
              currentPage: page,
              pageSize,
              setPage: updatePage,
              setPageSize: updatePageSize,
            }}
            rowClicked={goToIntegration}
            emptyText={t("translation:no_integrations_found")}
          />
        </Col>
      </Row>
    </div>
  );
}
